import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { AppDataService } from '../../core/services/app-data.service';
import { ReportService } from '../../core/services/report.service';
import { ExportService } from '../../core/services/export.service';
import { formatCurrency } from '../../core/utils/format.util';
import { matchesItemQuery } from '../../core/utils/item-filter.util';
import { environment } from '../../environments/environment';
import { ReportDateRangeComponent } from '../report-date-range/report-date-range.component';
import { ReportDateRangeService } from '../../report-date-range.service';

@Component({
  selector: 'app-stock-valuation-report',
  standalone: true,
  imports: [FormsModule, RouterLink, ReportDateRangeComponent],
  templateUrl: './stock-valuation.component.html',
  styleUrl: '../../report-shared.css',
})
export class StockValuationReportComponent implements OnInit {
  private readonly appData = inject(AppDataService);
  private readonly reports = inject(ReportService);
  private readonly exporter = inject(ExportService);
  protected readonly dateRange = inject(ReportDateRangeService);
  protected readonly formatCurrency = formatCurrency;

  private readonly range = computed(() => ({
    startDate: this.dateRange.appliedStartDate(),
    endDate: this.dateRange.appliedEndDate(),
  }));

  protected readonly summary = computed(() =>
    this.reports.getStockOnHandSummary(this.range()),
  );

  protected readonly categories = computed(() =>
    this.reports.getStockOnHandByCategory(this.range()),
  );

  protected readonly itemNameFilter = signal('');

  protected readonly items = computed(() =>
    this.reports
      .getStockValuation(this.range())
      .filter((row) =>
        matchesItemQuery(this.itemNameFilter(), row.itemName, row.itemCode),
      ),
  );

  protected categoryWidth(value: number): number {
    const total = this.summary().totalValue || 1;
    return Math.round((value / total) * 100);
  }

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.appData.refreshAll();
    }
  }

  exportData(): void {
    this.exporter.downloadCsv(
      `stock-valuation-${new Date().toISOString().slice(0, 10)}`,
      this.items().map((row) => ({ ...row })),
    );
  }

  printPage(): void {
    this.exporter.printPage();
  }
}
